import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from '@digital-twins/design-system/icons'
import { LibraryCollectionCard } from '@digital-twins/shared-types'

import { MosaicCollectionCard } from '@/components/library/MosaicCollectionCard'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import Link from '@/routing/Link'
import { ROUTES } from '@/routes/routes'

export interface LibraryCollectionsGridProps {
  collections: Array<LibraryCollectionCard>
  isLoading: boolean
  hasError: boolean
  onNewCollection: () => void
}

/** The Library home shows at most this many collections; See all opens the list page. */
export const LIBRARY_HOME_COLLECTIONS_LIMIT = 6

/** The card grid of the Library's collection blocks, sized by `@container/library`. */
export const LIBRARY_COLLECTION_GRID_CLASS =
  'grid grid-cols-1 gap-4 @[760px]/library:grid-cols-2 @[960px]/library:grid-cols-3'

export interface LibraryAreaTitleProps {
  title: string
  /** A muted 12 px note after the title (a count, a hint). */
  note?: ReactNode
  /** Right-aligned actions (See all). */
  trailing?: ReactNode
  testId: string
}

/** An area title in the Topic redesign's pattern: Sumana 24, a muted note, actions right. */
export function LibraryAreaTitle(props: LibraryAreaTitleProps) {
  const { title, note, trailing, testId } = props

  return (
    <div className="flex min-w-0 items-end justify-between gap-3" data-testid={testId}>
      <div className="flex min-w-0 items-baseline gap-3">
        <h2 className="truncate font-display text-[24px] font-normal leading-[1.1] text-foreground">{title}</h2>
        {note ? <span className="shrink-0 text-[12px] tabular-nums text-muted-foreground">{note}</span> : null}
      </div>
      {trailing ? <div className="flex shrink-0 items-center gap-2">{trailing}</div> : null}
    </div>
  )
}

export interface LibraryCollectionsSkeletonProps {
  count: number
  testId: string
}

/** Hairline outlines faded in after 150 ms, as the Topic page's skeletons. */
export function LibraryCollectionsSkeleton(props: LibraryCollectionsSkeletonProps) {
  const { count, testId } = props

  return (
    <div
      className={`${LIBRARY_COLLECTION_GRID_CLASS} animate-in fade-in fill-mode-both delay-150`}
      aria-hidden="true"
      data-testid={testId}
    >
      {Array.from({ length: count }, (_, index) => (
        <Skeleton
          key={index}
          className="h-[260px] rounded-[calc(var(--radius)+8px)] border border-card-border bg-background"
        />
      ))}
    </div>
  )
}

/**
 * The Library home's Collections block (D14): at most 6 `MosaicCollectionCard`s
 * and See all to the list page, or an empty state with New collection. A load
 * error hides the block, so the rest of the Library stays usable.
 */
export function LibraryCollectionsGrid(props: LibraryCollectionsGridProps) {
  const { collections, isLoading, hasError, onNewCollection } = props

  const { t } = useTranslation('app')

  const visible = collections.slice(0, LIBRARY_HOME_COLLECTIONS_LIMIT)

  if (hasError && collections.length === 0) {
    return null
  }

  const seeAll =
    collections.length > 0 ? (
      <Link
        href={ROUTES.knowledgeCollections}
        className="rounded-md text-sm text-muted-foreground transition-colors hover:text-foreground"
        data-testid="library-collections-see-all"
      >
        {t('library.collections.seeAll')}
      </Link>
    ) : null

  let body: ReactNode
  if (collections.length === 0 && isLoading) {
    body = <LibraryCollectionsSkeleton count={3} testId="library-collections-loading" />
  } else if (collections.length === 0) {
    body = (
      <div className="flex flex-wrap items-center gap-3" data-testid="library-collections-empty">
        <p className="text-sm text-muted-foreground">{t('library.collections.home.empty')}</p>
        <Button
          type="button"
          variant="outline"
          size="sm"
          shape="pill"
          className="h-8 px-3"
          onClick={onNewCollection}
          data-testid="library-collections-empty-new"
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" aria-hidden="true" />
          {t('library.collections.newCollection')}
        </Button>
      </div>
    )
  } else {
    body = (
      <div className={LIBRARY_COLLECTION_GRID_CLASS} data-testid="library-collections-grid">
        {visible.map((collection, index) => (
          <MosaicCollectionCard key={collection.token} collection={collection} index={index} />
        ))}
      </div>
    )
  }

  return (
    <section
      className="@container/library flex min-w-0 flex-col gap-4"
      aria-label={t('library.collections.title')}
      data-testid="library-collections"
    >
      <LibraryAreaTitle title={t('library.collections.title')} trailing={seeAll} testId="library-collections-header" />
      {body}
    </section>
  )
}
